import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../components/feature/BottomNavigation';
import toast from '../../utils/toast';

export default function RateUsPage() {
  const navigate = useNavigate();
  const [selectedRating, setSelectedRating] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleStarClick = (rating: number) => {
    setSelectedRating(rating);
  };

  const handleRateApp = () => {
    // Simular redirecionamento para loja de aplicativos
    setShowThankYou(true);
    setTimeout(() => {
      setShowThankYou(false);
      navigate('/settings');
    }, 3000);
  };

  const handleShareApp = () => {
    // Simular compartilhamento do app
    if (navigator.share) {
      navigator.share({
        title: 'Magistral - App de Homeopatia',
        text: 'Conheça o Magistral, o melhor app para acompanhar seu tratamento homeopático!',
        url: window.location.origin,
      });
    } else {
      // Fallback para navegadores que não suportam Web Share API
      navigator.clipboard.writeText(`Conheça o Magistral, o melhor app para acompanhar seu tratamento homeopático! ${window.location.origin}`);
      toast.success('Link copiado para a área de transferência!');
    }
  };

  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 mx-6 shadow-xl text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 mx-auto">
            <i className="ri-star-fill text-yellow-500 text-2xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Obrigado!</h2>
          <p className="text-gray-600 mb-6">Sua avaliação é muito importante para nós. Redirecionando para a loja...</p>
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between p-6">
          <button
            onClick={() => navigate('/settings')}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-gray-600 text-lg"></i>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Avalie-nos</h1>
          <div className="w-10 h-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {/* Main Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-yellow-100 rounded-3xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-star-line text-yellow-500 text-3xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Gostou do Magistral?</h2>
            <p className="text-gray-600 text-sm">
              Sua avaliação nos ajuda a melhorar e alcançar mais pessoas que precisam de cuidados homeopáticos.
            </p>
          </div>

          {/* Rating Stars */}
          <div className="mb-8">
            <p className="text-center text-sm font-medium text-gray-700 mb-4">
              Como você avaliaria nossa aplicação?
            </p>
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleStarClick(star)}
                  className="transition-transform hover:scale-110 active:scale-95"
                >
                  <i
                    className={`text-3xl ${
                      star <= selectedRating
                        ? 'ri-star-fill text-yellow-500'
                        : 'ri-star-line text-gray-300'
                    }`}
                  ></i>
                </button>
              ))}
            </div>
            {selectedRating > 0 && (
              <p className="text-center text-sm text-gray-600">
                {selectedRating === 5 && "Excelente! ⭐"}
                {selectedRating === 4 && "Muito bom! 👍"}
                {selectedRating === 3 && "Bom! 👌"}
                {selectedRating === 2 && "Pode melhorar 🤔"}
                {selectedRating === 1 && "Precisa melhorar 😔"}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleRateApp}
              disabled={selectedRating === 0}
              className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <i className="ri-star-line mr-2"></i>
              Avaliar na Loja de Apps
            </button>

            <button
              onClick={handleShareApp}
              className="w-full py-4 bg-blue-500 text-white rounded-2xl font-medium flex items-center justify-center"
            >
              <i className="ri-share-line mr-2"></i>
              Compartilhar com Amigos
            </button>
          </div>
        </div>

        {/* Statistics Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            Junte-se à nossa comunidade!
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-emerald-500 mb-1">10k+</div>
              <div className="text-xs text-gray-600">Usuários</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-500 mb-1">4.8</div>
              <div className="text-xs text-gray-600">Avaliação</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-500 mb-1">500+</div>
              <div className="text-xs text-gray-600">Remédios</div>
            </div>
          </div>
        </div>

        {/* Benefits Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-6 shadow-sm text-white">
          <h3 className="text-lg font-semibold mb-4">Por que avaliar?</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <i className="ri-check-line text-emerald-200 mr-3"></i>
              <span className="text-sm">Ajuda outros usuários a descobrir o app</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-emerald-200 mr-3"></i>
              <span className="text-sm">Nos motiva a continuar melhorando</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-emerald-200 mr-3"></i>
              <span className="text-sm">Contribui para a comunidade homeopática</span>
            </div>
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
}